<script setup lang="ts">
import type { BarChartData } from '~/components/nrjx/chart/bar/index.vue';
import dayjs from 'dayjs';
import { until } from '@vueuse/core';

// Composables
const { options } = useExploreBarChart();
const { getTailwindColor } = useTailwind();

// Store
const {
  consumptionResponse,
  productionResponse,
  loadingConsumption,
  loadingProduction,
  temperatureResponse,
  loadingTemperature,
  areasToCompare,
  comparisonResponse,
  loadingComparison,
} = storeToRefs(chronologicalStore());
const { aggregationLevel, showTemperature } = storeToRefs(exploreStore());

const props = defineProps<{
  type: 'consumption' | 'production';
}>();

// Data
const datasets = ref<BarChartData[]>([]);
const loading = ref(true);

// Methods
const createDatasets = (propsType: 'consumption' | 'production', showTemperature: boolean) => {
  const data: BarChartData[] = [];

  const temperatureDictionary = new Map<string, number>();
  const productionDictionary = new Map<string, number>();
  temperatureResponse.value?.items.forEach((temperature) => {
    temperatureDictionary.set(dayjs(temperature.date).format('DD/MM/YYYY'), temperature.average ?? 0);
  });
  productionResponse.value?.items.forEach((production) => {
    productionDictionary.set(dayjs(production.production.start).format('DD/MM/YYYY'), production.consumption_per_unit.quantity);
  });

  consumptionResponse.value?.items.map((item) => {
    const temperature = temperatureDictionary.get(dayjs(item.start).format('DD/MM/YYYY')) ?? 0;
    const production = productionDictionary.get(dayjs(item.start).format('DD/MM/YYYY')) ?? 0;
    data.push({
      key: dayjs(item.start).format('DD/MM/YYYY'),
      barValues: [propsType === 'consumption' ? { value: item.consumption.quantity } : { value: production }],
      lineValue: aggregationLevel.value === 'day' && showTemperature ? temperature : undefined,
    });
  });

  datasets.value = data;
};

const createComparisonDatasets = () => {
  if (!comparisonResponse.value?.compared_to || !comparisonResponse.value?.reference) return;
  const data: BarChartData[] = [];

  comparisonResponse.value.reference.items.forEach((item) => {
    data.push({
      key: dayjs(item.start).format('DD/MM/YYYY'),
      barValues: [
        { value: item.consumption.quantity, color: getTailwindColor('emerald-500') },
        ...(comparisonResponse.value?.compared_to?.map((comparedArea) => {
          const color = areasToCompare.value.find((compareArea) => compareArea.id === comparedArea.area_id);
          return {
            value: comparedArea.consumption.items.find((consumption) => consumption.start === item.start)?.consumption.quantity ?? 0,
            color: color?.color ?? getTailwindColor('emerald-500'),
          };
        }) ?? []),
      ],
      lineValue: undefined,
    });
  });

  datasets.value = data;
};

// Loading
watchEffect(async () => {
  loading.value = true;
  const showTemperatureValue = showTemperature.value;
  if (props.type === 'consumption' && areasToCompare.value.length === 0) {
    await until(loadingConsumption).not.toBe(true);
    if (aggregationLevel.value === 'day' && showTemperatureValue) await until(loadingTemperature).not.toBe(true);
    createDatasets(props.type, showTemperatureValue);
  } else if (props.type === 'consumption' && areasToCompare.value.length > 0) {
    await until(loadingComparison).not.toBe(true);
    createComparisonDatasets();
  } else if (props.type === 'production') {
    await until(loadingConsumption).not.toBe(true);
    await until(loadingProduction).not.toBe(true);
    if (aggregationLevel.value === 'day' && showTemperatureValue) await until(loadingTemperature).not.toBe(true);
    createDatasets(props.type, showTemperatureValue);
  }
  loading.value = false;
});
</script>
<template>
  <div>
    <nrjx-chart-bar :options="options" :loading="loading" :data="datasets" :clickable="false" />
  </div>
</template>
